import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import HeaderSecondary from 'flarum/forum/components/HeaderSecondary';
import UserControls from 'flarum/forum/utils/UserControls';
import PostControls from 'flarum/forum/utils/PostControls';
import Button from 'flarum/common/components/Button';
import MessagesPage from './components/MessagesPage.js';
import MessagesNavButton from './components/MessagesNavButton.js';
import MessagesComposeButton from './components/MessagesComposeButton.js';
import NotificationsPage from './components/NotificationsPage.js';
import MessagingState from './state/MessagingState.js';
import createMessagingService from './createMessagingService.js';
import canOfferMessageAction from './utils/canOfferMessageAction.js';
import { isUnifiedMessagesRoute } from './utils/messagingRoutes.js';
import IndexPage from 'flarum/forum/components/IndexPage';
import { bindExistingRealtime, createNotificationInbox, syncNotificationAccess } from './notifications/createNotificationInbox.js';
import { bindEligibilityRevalidation } from './notifications/eligibilityRevalidation.js';
import { notificationsAvailable } from './notifications/notificationsAvailability.js';
import { createNotificationsRouteComponent } from './notifications/notificationsRouteComponent.js';
app.initializers.add('flatrate-messaging-ui', () => {
  app.routes['flatrate-messaging.index'] = {
    path: '/messages',
    component: MessagesPage
  };
  app.routes['flatrate-messaging.live'] = {
    path: '/messages/live/:roomKey',
    component: MessagesPage
  };
  app.routes['flatrate-messaging.direct'] = {
    path: '/messages/direct/:conversationId',
    component: MessagesPage
  };
  if (app.routes.notifications && app.routes.notifications.component) {
    const nativeNotificationsPage = app.routes.notifications.component;
    app.routes.notifications.component = createNotificationsRouteComponent({
      app,
      nativeComponent: nativeNotificationsPage,
      unifiedComponent: NotificationsPage,
      render: (component, attrs) => m(component, attrs)
    });
    app.routes['flatrate-notifications.index'] = app.routes.notifications;
  } else if (notificationsAvailable(app)) {
    const notificationsRoute = {
      path: '/notifications',
      component: NotificationsPage
    };
    app.routes.notifications = notificationsRoute;
    app.routes['flatrate-notifications.index'] = notificationsRoute;
  }
  app.flatrateMessagingState = new MessagingState({
    sources: () => app.flatrateMessaging ? app.flatrateMessaging.sources() : {
      live: null,
      direct: null
    }
  });
  app.flatrateMessaging = createMessagingService({
    app,
    state: app.flatrateMessagingState
  });
  const inbox = createNotificationInbox(app);
  app.flatrateNotificationState = inbox.state;
  app.flatrateNotifications = inbox;
  bindExistingRealtime(app, inbox);
  bindSessionLifecycle(app, inbox);
  bindEligibilityRevalidation(app, inbox);
  syncNotificationAccess(app, inbox.state);
  extend(IndexPage.prototype, 'oninit', function onMainEligibility() {
    if (inbox.revalidate) inbox.revalidate('main');
  });
  setTimeout(() => {
    if (app.session && app.session.user && notificationsAvailable(app)) {
      // The header plane reads this count during view. Refresh settles after
      // the first paint, so redraw or the idle white icon stays up over a known unread count.
      inbox.refresh().then(() => m.redraw());
    }
  }, 0);
  extend(HeaderSecondary.prototype, 'items', function (items) {
    if (!app.session.user) {
      return;
    }
    items.add('FlatRateMessages', m(MessagesNavButton, null), 5);
    if (isUnifiedMessagesRoute()) {
      items.add('FlatRateMessagesCompose', m(MessagesComposeButton, null), 4);
    }
  });
  extend(UserControls, 'userControls', function (items, user) {
    if (!canOfferMessageAction({
      actor: app.session.user,
      targetUser: user,
      canMessage: !!app.forum.attribute('canMessage')
    })) {
      return;
    }
    items.add('flatrateMessage', m(Button, {
      icon: "fas fa-paper-plane",
      onclick: () => app.flatrateMessaging.openDirectToUser(user)
    }, app.translator.trans('flatrate-messaging-ui.forum.profile.message')), 90);
  });
  extend(PostControls, 'userControls', function (items, post) {
    const target = post && typeof post.user === 'function' ? post.user() : null;
    if (!canOfferMessageAction({
      actor: app.session.user,
      targetUser: target,
      canMessage: !!app.forum.attribute('canMessage')
    })) {
      return;
    }
    items.add('flatrateMessage', m(Button, {
      icon: "fas fa-paper-plane",
      onclick: () => app.flatrateMessaging.openDirectToUser(target)
    }, app.translator.trans('flatrate-messaging-ui.forum.actions.message')));
  });
});
function bindSessionLifecycle(app, inbox) {
  const session = app.session;
  if (!session) return;
  if (typeof session.logout === 'function' && !session.logout.flatrateNotificationsBound) {
    const logout = session.logout.bind(session);
    const wrapped = function () {
      inbox.state.setSignedIn(false);
      return logout(...arguments);
    };
    wrapped.flatrateNotificationsBound = true;
    session.logout = wrapped;
  }
  if (typeof session.login === 'function' && !session.login.flatrateNotificationsBound) {
    const login = session.login.bind(session);
    const wrapped = function (user) {
      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }
      const result = login(user, ...args);
      syncNotificationAccess(app, inbox.state);
      return result;
    };
    wrapped.flatrateNotificationsBound = true;
    session.login = wrapped;
  }
}